import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { gql } from "@apollo/client";
import { useLibraryItemDataTransformMutation } from "./useLibraryItemDataTransform.generated";
import { ChatAttachmentLibraryItemFragment } from "@src/features/assistantChat/aiChat.generated";
import { DataTransformType } from "@src/types.generated";

export interface LibraryItemTransform {
  uuid: string;
  state: "transforming" | "success" | "error";
  libraryItemId: string;
  libraryItemFragment?: ChatAttachmentLibraryItemFragment;
  error?: string;
}

export interface UseLibraryItemDataTransform {
  transformLibraryItem: (
    libraryItem: ChatAttachmentLibraryItemFragment,
  ) => void;
}

export function useLibraryItemDataTransform({
  onTransformStarted,
  onTransformCompleted,
  onTransformError,
}: {
  onTransformStarted: (libraryItemTransform: LibraryItemTransform) => void;
  onTransformCompleted: (libraryItemTransform: LibraryItemTransform) => void;
  onTransformError: (libraryItemTransform: LibraryItemTransform) => void;
}): UseLibraryItemDataTransform {
  const [createOrUpdateDataTransform] = useLibraryItemDataTransformMutation();

  const transformLibraryItem = useCallback(
    async (libraryItem: ChatAttachmentLibraryItemFragment) => {
      const uuid = uuidv4();
      const transformingItem: LibraryItemTransform = {
        uuid,
        state: "transforming",
        libraryItemId: libraryItem.id,
        libraryItemFragment: libraryItem,
      };

      onTransformStarted(transformingItem);

      try {
        const { data } = await createOrUpdateDataTransform({
          variables: {
            input: {
              libraryItemId: libraryItem.id,
              type: DataTransformType.AiChatAttachmentMarkdown,
            },
          },
        });

        if (!data?.createOrUpdateDataTransform?.success) {
          const errorItem: LibraryItemTransform = {
            ...transformingItem,
            state: "error",
            error:
              data?.createOrUpdateDataTransform?.error?.code ||
              "Transform failed",
          };
          onTransformError(errorItem);
          return;
        }

        const successItem: LibraryItemTransform = {
          ...transformingItem,
          state: "success",
        };
        onTransformCompleted(successItem);
      } catch (error) {
        const errorItem: LibraryItemTransform = {
          ...transformingItem,
          state: "error",
          error: error instanceof Error ? error.message : "Transform failed",
        };
        onTransformError(errorItem);
      }
    },
    [
      createOrUpdateDataTransform,
      onTransformStarted,
      onTransformCompleted,
      onTransformError,
    ],
  );

  return {
    transformLibraryItem,
  };
}

gql`
  mutation LibraryItemDataTransform($input: DataTransformInput!) {
    createOrUpdateDataTransform(input: $input) {
      success
      dataTransform {
        id
        output {
          en
        }
      }
      error {
        code
      }
    }
  }
`;
